// Lecturas y acciones de administración de la bitácora de cables (estado,
// inspecciones, hallazgos, reportes). Reglas:
// - Lecturas por período (`fecha` entre desde y hasta) y paginadas de 1000 en
//   1000: PostgREST corta en 1000 filas y un corte silencioso haría mentir a
//   los indicadores y al paquete de auditoría.
// - Todo se lee por instalación (canopy | tarzan_swing | canyoning). Si la base todavía no
//   tiene la columna `instalacion`, se degrada a canopy sin romper.
// - Un error SIEMPRE se lanza (nunca se convierte en "sin datos"): la pantalla
//   lo muestra como error con Reintentar.
// - Nadie edita ni borra: admin anula con motivo; los días sin operación se
//   marcan y, si hay error, se anulan con motivo.
import { supabase } from 'lib/supabase';
import { motivoValido } from 'lib/auditoria';
import { idInspeccionValido } from 'lib/auditoriaInspeccion';
import { mensajeError, tipoError } from 'lib/errores';

const TABLE = 'cables_inspecciones';
const SIN_OP = 'cables_dias_sin_operacion';
const REPORTES = 'cables_reportes';
export const BUCKET = 'cables-evidencia';
export const PAGINA = 1000;

const esErrorDeRed = (e) => {
  const t = `${e?.message || ''} ${e?.name || ''}`;
  return (typeof navigator !== 'undefined' && navigator.onLine === false)
    || /Failed to fetch|NetworkError|Load failed|TypeError|network/i.test(t);
};

// Mensaje para la persona. Conserva el error original en `causa`.
export function errorAmigable(e, contexto = 'No se pudo completar la acción') {
  if (e?.amigable) return e;
  let msg;
  if (esErrorDeRed(e)) msg = `${contexto}: sin conexión con el servidor. Revise la conexión e intente de nuevo.`;
  else if (e?.code === '42501' || /permission|row-level security|not allowed/i.test(e?.message || '')) {
    msg = `${contexto}: su usuario no tiene permiso para esta acción.`;
  } else if (e?.code === '42P01' || e?.code === 'PGRST205' || e?.code === 'PGRST202' || /does not exist|Could not find/i.test(e?.message || '')) {
    msg = `${contexto}: la base de datos todavía no tiene esta función (falta aplicar la actualización).`;
  } else if (!e?.message) msg = `${contexto}.`;
  // Un texto propio en español (RAISE de nuestras funciones) se conserva como
  // antes; el resto (sesión vencida, tiempo agotado, texto técnico en inglés)
  // pasa por el traductor común de shared (auditoría de UI 2026-10-20, Lote 2).
  else if (tipoError(e) === 'propio') msg = `${contexto}: ${mensajeError(e)}`;
  else msg = `${contexto}. ${mensajeError(e, 'Intente de nuevo.')}`;
  const err = new Error(msg);
  err.amigable = true;
  err.causa = e;
  return err;
}

export async function leerPaginado(construir, contexto) {
  const out = [];
  for (let desde = 0; ; desde += PAGINA) {
    let res;
    try {
      res = await construir().range(desde, desde + PAGINA - 1);
    } catch (e) {
      throw errorAmigable(e, contexto);
    }
    if (res?.error) throw errorAmigable(res.error, contexto);
    const data = res?.data || [];
    out.push(...data);
    if (data.length < PAGINA) return out;
  }
}

export async function leer(construir, contexto) {
  let res;
  try {
    res = await construir();
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  if (res?.error) throw errorAmigable(res.error, contexto);
  return res?.data || [];
}

// ── Instalación ──
// La primera consulta que falle porque la columna `instalacion` no existe lo
// anota aquí y las siguientes ya no filtran. Pedir otra instalación sin la
// columna devuelve vacío (no hay filas suyas), nunca las del canopy.
let baseSinInstalacion = false;
export const _reiniciarSoporteInstalacion = () => { baseSinInstalacion = false; };

export const faltaColumnaInstalacion = (e) => {
  const x = e?.causa || e;
  const t = `${x?.message || ''} ${x?.details || ''} ${x?.hint || ''}`;
  return (x?.code === '42703' || x?.code === 'PGRST204' || x?.code === 'PGRST202' || /column|columna|function/i.test(t))
    && /instalacion/i.test(t);
};

async function conInstalacion(instalacion, consultar, vacio) {
  if (!instalacion || baseSinInstalacion) {
    if (instalacion && instalacion !== 'canopy') return vacio;
    return consultar(false);
  }
  try {
    return await consultar(true);
  } catch (e) {
    if (!faltaColumnaInstalacion(e)) throw e;
    baseSinInstalacion = true;
    return instalacion === 'canopy' ? consultar(false) : vacio;
  }
}

const leerPorPeriodo = (tabla, orden, contexto) => ({ desde, hasta, instalacion } = {}) =>
  conInstalacion(instalacion, (filtrar) => leerPaginado(() => {
    let q = supabase.from(tabla).select('*');
    if (desde) q = q.gte('fecha', desde);
    if (hasta) q = q.lte('fecha', hasta);
    if (filtrar) q = q.eq('instalacion', instalacion);
    return orden(q);
  }, contexto), []);

export const fetchInspeccionesPeriodo = leerPorPeriodo(TABLE,
  (q) => q.order('fecha', { ascending: false }).order('fecha_hora', { ascending: false }).order('id'),
  'No se pudieron cargar las inspecciones');

export const fetchDiasSinOperacion = leerPorPeriodo(SIN_OP,
  (q) => q.order('fecha', { ascending: false }).order('id'),
  'No se pudieron cargar los días que no se operó');

// Paquetes de auditoría generados (cables_reportes), del más nuevo al más
// viejo. Con instalación: los de esa instalación y los de "todas" (null).
export const fetchReportes = ({ instalacion, limite = 50 } = {}) =>
  conInstalacion(instalacion, (filtrar) => leer(() => {
    let q = supabase.from(REPORTES).select('*');
    if (filtrar) q = q.or(`instalacion.eq.${instalacion},instalacion.is.null`);
    return q.order('generado_at', { ascending: false }).limit(limite);
  }, 'No se pudieron cargar los reportes generados'), []);

// Nombres de usuarios de Auth: { uuid: nombre }. RPC cables_nombres (nombre
// del colaborador de RRHH o, si no está vinculado, el correo). Nunca lanza: si
// la RPC no existe todavía o falla, devuelve lo que tenga y la pantalla
// muestra el UUID corto. Máximo 200 ids por llamada (límite de la RPC).
export async function fetchNombres(ids) {
  const unicos = [...new Set((ids || []).filter(Boolean))];
  const out = {};
  for (let i = 0; i < unicos.length; i += 200) {
    try {
      const { data, error } = await supabase.rpc('cables_nombres', { p_ids: unicos.slice(i, i + 200) });
      if (error) return out;
      for (const f of data || []) if (f?.id && f?.nombre) out[f.id] = f.nombre;
    } catch {
      return out;
    }
  }
  return out;
}

// Detalle de UNA inspección y las correcciones que la apuntan (corrige_a).
// Sin fila = { inspeccion: null } ("no encontrada"; también la que RLS no deja
// ver). Un error de red o de permiso se LANZA: no es lo mismo que no existir.
export async function fetchInspeccionDetalle(id) {
  if (!idInspeccionValido(id)) return { inspeccion: null, corregidaPor: [] };
  const contexto = 'No se pudo cargar la inspección';
  const [filas, correcciones] = await Promise.all([
    leer(() => supabase.from(TABLE).select('*').eq('id', id), contexto),
    leer(() => supabase.from(TABLE)
      .select('id, fecha, fecha_hora, created_at, guia_nombre, estado_general, anulada, corrige_a')
      .eq('corrige_a', id).order('created_at', { ascending: true }), contexto),
  ]);
  const inspeccion = filas[0] || null;
  return { inspeccion, corregidaPor: inspeccion ? correcciones : [] };
}

export const exigirMotivo = (motivo) => {
  if (!motivoValido(motivo)) {
    const e = new Error('Escriba un motivo de al menos 5 caracteres.');
    e.amigable = true;
    throw e;
  }
  return String(motivo).trim();
};

// UPDATE con RLS que no aplica no da error: devuelve 0 filas. Se pide la fila
// de vuelta para distinguir "anulada" de "no se pudo".
export async function anular(tabla, id, motivo, contexto) {
  const m = exigirMotivo(motivo);
  let res;
  try {
    res = await supabase.from(tabla).update({ anulada: true, anulada_motivo: m }).eq('id', id).select('*');
  } catch (e) {
    throw errorAmigable(e, contexto);
  }
  if (res?.error) throw errorAmigable(res.error, contexto);
  const fila = (res?.data || [])[0];
  if (!fila) throw errorAmigable({ code: '42501', message: 'permission' }, contexto);
  return fila;
}

export const anularInspeccion = (id, motivo) =>
  anular(TABLE, id, motivo, 'No se pudo anular la inspección');

export const anularDiaSinOperacion = (id, motivo) =>
  anular(SIN_OP, id, motivo, 'No se pudo anular la marca');

// Marca un día sin operación de UNA instalación (sin instalación = como antes,
// la base pone canopy por defecto).
export async function marcarDiaSinOperacion(fecha, motivo, instalacion) {
  const m = exigirMotivo(motivo);
  const contexto = 'No se pudo marcar el día sin operación';
  const insertar = async (conInst) => {
    let res;
    try {
      res = await supabase.from(SIN_OP).insert(conInst ? { fecha, motivo: m, instalacion } : { fecha, motivo: m }).select('*');
    } catch (e) {
      throw errorAmigable(e, contexto);
    }
    if (res?.error) {
      if (res.error.code === '23505') {
        const e = new Error(`${contexto}: ese día ya está marcado.`);
        e.amigable = true;
        throw e;
      }
      throw errorAmigable(res.error, contexto);
    }
    return (res?.data || [])[0] || null;
  };
  if (!instalacion) return insertar(false);
  return conInstalacion(instalacion, insertar, null).then((fila) => {
    if (fila === null && instalacion !== 'canopy' && baseSinInstalacion) {
      throw errorAmigable({ code: '42P01', message: 'does not exist' }, contexto);
    }
    return fila;
  });
}

// Registra el paquete en la base y devuelve { folio, generado_at } del servidor.
// Con instalación manda p_instalacion; si la base todavía tiene la RPC sin ese
// parámetro, el canopy se registra igual (sin él) y otra instalación falla.
export async function registrarReporte({ tipo, desde, hasta, sha256, conteos, instalacion }) {
  const contexto = 'No se pudo registrar el reporte';
  const llamar = async (conInst) => {
    const params = { p_tipo: tipo, p_desde: desde, p_hasta: hasta, p_sha256: sha256, p_conteos: conteos };
    if (conInst) params.p_instalacion = instalacion;
    let res;
    try {
      res = await supabase.rpc('cables_registrar_reporte', params);
    } catch (e) {
      throw errorAmigable(e, contexto);
    }
    if (res?.error) throw errorAmigable(res.error, contexto);
    const fila = Array.isArray(res?.data) ? res.data[0] : res?.data;
    if (!fila?.folio) throw errorAmigable({ message: 'el servidor no devolvió folio' }, contexto);
    return { folio: fila.folio, generado_at: fila.generado_at };
  };
  if (!instalacion) return llamar(false);
  try {
    return await llamar(true);
  } catch (e) {
    const c = e?.causa;
    const firmaVieja = c?.code === 'PGRST202' || faltaColumnaInstalacion(e);
    if (!firmaVieja || instalacion !== 'canopy') throw e;
    return llamar(false);
  }
}

// Firma varias rutas en UNA llamada. Devuelve { ruta: url|null }. Nunca lanza:
// una foto que no firma se muestra como "no disponible", no rompe la lista.
export async function signedUrls(paths) {
  const unicas = [...new Set((paths || []).filter(Boolean))];
  const out = {};
  if (!unicas.length) return out;
  try {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(unicas, 3600);
    if (error) throw error;
    for (const d of data || []) if (d?.path) out[d.path] = d.signedUrl || null;
  } catch {
    /* sin URLs: el llamador muestra "foto no disponible" */
  }
  for (const p of unicas) if (!(p in out)) out[p] = null;
  return out;
}
